import { useEffect, useRef, useState, type JSX } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Button, toast } from '@caratuva/ui';
import {
  createKybTosSession,
  getKybReceiverDetail,
  getKybSubmission,
  getKybTosStatus,
  getReferralStatus,
  initiateKyb,
  recordKybTosAcceptance,
  resetOnboarding,
  resubmitKyb,
  updateKyb,
  uploadKybFile,
  type InitiateKybInput,
  type KybReceiverDetailView,
  type KybReceiverWarning,
  type KybSubmissionView,
  type OwnerRole,
} from '../lib/api';
import {
  KybForm,
  KycForm,
  emptyBiz,
  emptyKyc,
  emptyOwner,
  kybValueToInitiateKyb,
  kybValueToUpdateKyb,
  kycValueToInitiateKyb,
  type BizFormValue,
  type OwnerFormValue,
  type UploadKind,
} from '../components/kyb-form';
import { KybReceiverPanel, KybStatusBadge, toDateInput } from '../components/kyb-receiver-panel';
import { ProductionAccessSection } from '../components/production-access-section';
import { ReferralCard } from '../components/referral-card';
import { ReferralPrompt } from '../components/referral-prompt';
import { getMode } from '../lib/auth';

/*
 * Onboarding — the org's OWN identity verification (ToS acceptance → business
 * KYB) with the settlement partner. Split out of the former Payout page; the
 * PIX / bank-account step now lives on its own /bank-account route. Once KYB is
 * approved, the org registers where settlements land there.
 *
 * Verification is only REQUIRED to operate in live mode (the API gates money
 * movement, not dashboard/API use in test mode), so this page is reachable in
 * both modes but is a prerequisite for going live, not for using the product.
 *
 * The page also hosts the production-access request section (formerly the
 * /go-live route), so an org can request live access once verification is
 * underway. The header ModeSwitcher deep-links here via #production-access
 * when an unapproved org tries to switch to live mode.
 */

// The Onboarding page uploads through the org-KYB signed-URL endpoint; the
// shared FileField takes the upload fn as a prop so seller/buyer can swap in
// their own.
function uploadOrgKybFile(file: File, kind: UploadKind, ownerIndex?: number): Promise<string> {
  return uploadKybFile(file, kind, ownerIndex);
}

export const Route = createFileRoute('/_app/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const tosQuery = useQuery({
    queryKey: ['kyb-tos'],
    queryFn: ({ signal }) => getKybTosStatus(signal),
  });

  const kybQuery = useQuery({
    queryKey: ['kyb-submission'],
    queryFn: ({ signal }) => getKybSubmission(signal),
    // While BlindPay is still reviewing, poll so the card flips to the approved
    // state on its own — no manual refresh. In test mode the dev-instance
    // webhook approves within seconds. Stop once the submission reaches a
    // terminal state (approved/rejected), or when there is none yet.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'submitted' || status === 'verifying'
        ? 4000
        : false;
    },
    refetchIntervalInBackground: false,
  });

  // The live BlindPay receiver detail backs the read-only panel (approved /
  // pending) and the rejection warnings + form pre-fill. Enabled once a
  // submission exists; never gates the page (ToS/initiate steps render even if
  // this 404s). Polls alongside the submission while review is in flight.
  const kybStatus = kybQuery.data?.status ?? null;
  const detailQuery = useQuery({
    queryKey: ['kyb-receiver-detail'],
    queryFn: ({ signal }) => getKybReceiverDetail(signal),
    enabled: kybQuery.data != null,
    refetchInterval: () =>
      kybStatus === 'pending' || kybStatus === 'submitted' || kybStatus === 'verifying'
        ? 4000
        : false,
    refetchIntervalInBackground: false,
  });

  const referralQuery = useQuery({
    queryKey: ['referral-status'],
    queryFn: ({ signal }) => getReferralStatus(signal),
  });

  // Capture the tos_id BlindPay appends to the redirect URL after the org
  // accepts the terms on app.blindpay.com. We strip it from the address bar
  // immediately (so a refresh can't re-fire) and record it server-side; the
  // record call is idempotent on the org's stored tosId.
  const recordTos = useMutation({
    mutationFn: (tosId: string) => recordKybTosAcceptance(tosId),
    onSuccess: () => {
      // Strip the redirect param only after acceptance is durably recorded, so
      // a failed record can still be retried by refreshing the page. The record
      // call is idempotent server-side, so a mid-flight refresh is safe.
      const params = new URLSearchParams(window.location.search);
      if (params.has('tos_id')) {
        params.delete('tos_id');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      }
      void qc.invalidateQueries({ queryKey: ['kyb-tos'] });
      toast.success(
        intl.formatMessage({
          id: 'payout.tos.recorded',
          defaultMessage: 'Settlement partner terms accepted.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const tosCaptureStarted = useRef(false);
  useEffect(() => {
    if (tosCaptureStarted.current) return;
    const tosId = new URLSearchParams(window.location.search).get('tos_id');
    if (!tosId) return;
    // Guard against React StrictMode's double-invoked mount effect firing two
    // record calls; a real page refresh resets the ref and retries if needed.
    tosCaptureStarted.current = true;
    recordTos.mutate(tosId);
  }, []);

  const isLoading = tosQuery.isLoading || kybQuery.isLoading;
  const isError = tosQuery.isError || kybQuery.isError;

  return (
    <section className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'onboarding.title', defaultMessage: 'Onboarding' })}
        </h1>
        <p className="mt-1 text-sm text-navy-600">
          {intl.formatMessage({
            id: 'onboarding.description',
            defaultMessage:
              'Accept our settlement partner’s terms and verify your business. This is required to enable live mode — you can use test mode without it. Once verified, register where settlements land on the Bank account page.',
          })}
        </p>
      </header>

      <ReferralPrompt />

      {isLoading ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({ id: 'payout.loading', defaultMessage: 'Loading...' })}
        </p>
      ) : isError ? (
        <p className="text-sm text-red-700">
          {intl.formatMessage({
            id: 'onboarding.error.load',
            defaultMessage: 'Could not load onboarding status. Refresh to try again.',
          })}
        </p>
      ) : (
        <>
          <OnboardingBody
            tosAccepted={Boolean(tosQuery.data?.accepted)}
            kyb={kybQuery.data ?? null}
            detail={detailQuery.data ?? null}
          />
          {referralQuery.data ? (
            <div className="max-w-2xl">
              <ReferralCard
                status={referralQuery.data}
                onApplied={() => void referralQuery.refetch()}
              />
            </div>
          ) : null}
        </>
      )}

      <ProductionAccessSection />

      {/* Test-mode escape hatch: reset onboarding to re-run the flow. Hidden in
          live mode (the server also hard-refuses a live reset). */}
      {!isLoading && !isError && getMode() === 'test' ? <TestResetPanel /> : null}
    </section>
  );
}

// Test-mode-only control to wipe this org's test onboarding and re-run
// ToS -> KYB (-> Bank account). Invalidates the queries the flow depends on so
// it re-renders from the top without a full reload.
function TestResetPanel(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const reset = useMutation({
    mutationFn: () => resetOnboarding(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyb-tos'] });
      void qc.invalidateQueries({ queryKey: ['kyb-submission'] });
      void qc.invalidateQueries({ queryKey: ['kyb-receiver-detail'] });
      void qc.invalidateQueries({ queryKey: ['payout-config'] });
      toast.success(
        intl.formatMessage({
          id: 'payout.reset.done',
          defaultMessage: 'Test onboarding reset — run the flow again above.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="max-w-xl rounded-md border border-dashed border-navy-300 bg-navy-50/50 p-4">
      <h2 className="text-sm font-semibold text-navy-900">
        {intl.formatMessage({ id: 'payout.reset.title', defaultMessage: 'Test tools' })}
      </h2>
      <p className="mt-1 text-xs text-navy-600">
        {intl.formatMessage({
          id: 'onboarding.reset.body',
          defaultMessage:
            'Reset this organization’s test-mode onboarding to run the terms → verification flow again. Live data is never affected.',
        })}
      </p>
      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={reset.isPending}
          onClick={() => {
            const ok = window.confirm(
              intl.formatMessage({
                id: 'payout.reset.confirm',
                defaultMessage: 'Reset test-mode onboarding and re-run the KYB flow?',
              }),
            );
            if (ok) reset.mutate();
          }}
        >
          {reset.isPending
            ? intl.formatMessage({ id: 'payout.reset.pending', defaultMessage: 'Resetting…' })
            : intl.formatMessage({
                id: 'payout.reset.cta',
                defaultMessage: 'Reset onboarding (test)',
              })}
        </Button>
      </div>
    </div>
  );
}

function OnboardingBody({
  tosAccepted,
  kyb,
  detail,
}: {
  tosAccepted: boolean;
  kyb: KybSubmissionView | null;
  detail: KybReceiverDetailView | null;
}): JSX.Element {
  // Onboarding order: accept BlindPay ToS -> KYB. The ToS must come first
  // because creating the org's BlindPay receiver (KYB) requires an accepted
  // tos_id. Once approved, the bank account is configured on /bank-account.
  if (!tosAccepted) return <TosAcceptCard />;
  if (!kyb) return <KybInitiateCard />;
  if (kyb.status === 'rejected') return <KybRejectedCard kyb={kyb} detail={detail} />;
  if (kyb.status !== 'approved') return <KybPendingCard kyb={kyb} detail={detail} />;
  return <KybApprovedCard detail={detail} entityType={kyb.entityType} />;
}

function TosAcceptCard(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const start = useMutation({
    mutationFn: () => createKybTosSession(`${window.location.origin}/onboarding`),
    onSuccess: async (session) => {
      // The in-memory dev mock returns a non-navigable URL on the
      // `mock.blindpay.local` host. Short-circuit by recording a synthetic
      // tos_id bound to the session we just minted, so local dev doesn't need a
      // real BlindPay round-trip.
      if (session.url.includes('mock.blindpay.local')) {
        try {
          await recordKybTosAcceptance(`tos_mock_${session.idempotencyKey}`);
          await qc.invalidateQueries({ queryKey: ['kyb-tos'] });
          toast.success(
            intl.formatMessage({
              id: 'payout.tos.recorded',
              defaultMessage: 'Settlement partner terms accepted.',
            }),
          );
        } catch (err) {
          toast.error(err instanceof Error ? err.message : String(err));
        }
        return;
      }
      // Real BlindPay: hand off to the hosted ToS page. It redirects back to
      // /onboarding with `?tos_id=...`, captured by OnboardingPage.
      window.location.href = session.url;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="max-w-xl space-y-4 rounded-md border border-navy-200 bg-white p-4">
      <div>
        <h2 className="text-base font-semibold text-navy-900">
          {intl.formatMessage({
            id: 'payout.tos.title',
            defaultMessage: 'Step 1 — Accept settlement partner terms',
          })}
        </h2>
        <p className="mt-1 text-sm text-navy-600">
          {intl.formatMessage({
            id: 'payout.tos.subtitle',
            defaultMessage:
              'Review and accept our regulated settlement partner’s terms of service to open your settlement account before we verify your business.',
          })}
        </p>
      </div>
      <div className="flex justify-end">
        <Button type="button" disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending
            ? intl.formatMessage({ id: 'payout.tos.opening', defaultMessage: 'Opening...' })
            : intl.formatMessage({
                id: 'payout.tos.accept',
                defaultMessage: 'Review & accept settlement partner terms',
              })}
        </Button>
      </div>
    </div>
  );
}

function isOwnerRole(r: string | null | undefined): r is OwnerRole {
  return r === 'beneficial_owner' || r === 'beneficial_controlling' || r === 'controlling_person';
}

// Seed the business form from the live receiver detail. Document fields are
// intentionally left empty — we never expose stored document URLs, so a
// resubmit re-uploads them (missingDocs enforces it).
function bizFromDetail(detail: KybReceiverDetailView | null): BizFormValue {
  const base = emptyBiz();
  if (!detail) return base;
  return {
    ...base,
    legalName: detail.legalName ?? '',
    cnpj: detail.taxId ?? '',
    email: detail.email ?? '',
    formationDate: toDateInput(detail.formationDate),
    website: detail.website ?? '',
    addressLine1: detail.address?.line1 ?? '',
    addressLine2: detail.address?.line2 ?? '',
    city: detail.address?.city ?? '',
    stateProvinceRegion: detail.address?.stateProvinceRegion ?? '',
    postalCode: detail.address?.postalCode ?? '',
    country: detail.address?.country ?? base.country,
  };
}

// Seed owner rows from the receiver detail. The receiver summary carries only
// identity fields; owner address / ID-doc / document fields are re-entered.
function ownersFromDetail(detail: KybReceiverDetailView | null): OwnerFormValue[] {
  if (!detail || detail.owners.length === 0) return [emptyOwner()];
  return detail.owners.map((o) => {
    const base = emptyOwner();
    return {
      ...base,
      role: isOwnerRole(o.role) ? o.role : base.role,
      firstName: o.firstName ?? '',
      lastName: o.lastName ?? '',
      dateOfBirth: toDateInput(o.dateOfBirth),
      taxId: o.taxId ?? '',
      country: o.country ?? base.country,
    };
  });
}

// Step 2, first attempt: the org chooses whether it verifies as a business
// (KYB) or as an individual (KYC), then fills the matching blank form that
// creates the BlindPay receiver. Both paths share the one initiate mutation.
function KybInitiateCard(): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();
  const [entityType, setEntityType] = useState<'individual' | 'business'>('business');

  const initiate = useMutation({
    mutationFn: (payload: InitiateKybInput) => initiateKyb(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyb-submission'] });
      void qc.invalidateQueries({ queryKey: ['kyb-receiver-detail'] });
      toast.success(
        intl.formatMessage({
          id: 'payout.kyb.submitted',
          defaultMessage:
            'Verification submitted. We will email you when our settlement partner finishes review.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const submitLabel = intl.formatMessage({
    id: 'payout.kyb.submit',
    defaultMessage: 'Submit for verification',
  });
  const submittingLabel = intl.formatMessage({
    id: 'payout.kyb.submitting',
    defaultMessage: 'Submitting...',
  });

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-md border border-navy-200 bg-white p-4">
        <p className="text-sm font-semibold text-navy-900">
          {intl.formatMessage({
            id: 'onboarding.entityType',
            defaultMessage: 'How do you verify?',
          })}
        </p>
        <p className="mt-1 text-sm text-navy-600">
          {intl.formatMessage({
            id: 'onboarding.entityType.hint',
            defaultMessage:
              'Verify your organization as a business (KYB) or as an individual (KYC).',
          })}
        </p>
        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            variant={entityType === 'business' ? 'default' : 'outline'}
            onClick={() => setEntityType('business')}
          >
            {intl.formatMessage({
              id: 'onboarding.entityType.business',
              defaultMessage: 'Business',
            })}
          </Button>
          <Button
            type="button"
            variant={entityType === 'individual' ? 'default' : 'outline'}
            onClick={() => setEntityType('individual')}
          >
            {intl.formatMessage({
              id: 'onboarding.entityType.individual',
              defaultMessage: 'Individual',
            })}
          </Button>
        </div>
      </div>

      {entityType === 'business' ? (
        <KybForm
          initialBiz={emptyBiz()}
          initialOwners={[emptyOwner()]}
          submitting={initiate.isPending}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
          heading={
            <div>
              <h2 className="text-base font-semibold text-navy-900">
                {intl.formatMessage({
                  id: 'payout.kyb.title',
                  defaultMessage: 'Step 2 — Verify your business',
                })}
              </h2>
              <p className="mt-1 text-sm text-navy-600">
                {intl.formatMessage({
                  id: 'payout.kyb.subtitle',
                  defaultMessage:
                    'Our settlement partner onboards your legal entity so it can settle BRL on your behalf via PIX. All fields and documents are required for verification.',
                })}
              </p>
            </div>
          }
          upload={uploadOrgKybFile}
          onSubmit={(value) => initiate.mutate(kybValueToInitiateKyb(value))}
        />
      ) : (
        <KycForm
          initialValue={{ ...emptyKyc() }}
          submitting={initiate.isPending}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
          heading={
            <div>
              <h2 className="text-base font-semibold text-navy-900">
                {intl.formatMessage({
                  id: 'onboarding.kyc.title',
                  defaultMessage: 'Step 2 — Verify your identity',
                })}
              </h2>
              <p className="mt-1 text-sm text-navy-600">
                {intl.formatMessage({
                  id: 'onboarding.kyc.subtitle',
                  defaultMessage:
                    'Our settlement partner verifies your identity so you can settle BRL via PIX. All fields and documents are required for verification.',
                })}
              </p>
            </div>
          }
          upload={uploadOrgKybFile}
          onSubmit={(value) => initiate.mutate(kycValueToInitiateKyb(value))}
        />
      )}
    </div>
  );
}

// Correct-and-resubmit after a rejection, posting to the resubmit endpoint
// (updates the existing receiver). Documents must be re-uploaded. The form
// matches the submission's entity type: business (KYB) pre-fills from the live
// receiver detail; individual (KYC) re-enters identity with a minimal email
// prefill (the receiver detail is business-shaped).
function KybResubmitForm({
  entityType,
  detail,
}: {
  entityType: 'individual' | 'business';
  detail: KybReceiverDetailView | null;
}): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const resubmit = useMutation({
    mutationFn: (payload: InitiateKybInput) => resubmitKyb(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyb-submission'] });
      void qc.invalidateQueries({ queryKey: ['kyb-receiver-detail'] });
      toast.success(
        intl.formatMessage({
          id: 'payout.kyb.resubmit.done',
          defaultMessage: 'Resubmitted — our settlement partner is reviewing again.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const submitLabel = intl.formatMessage({
    id: 'payout.kyb.resubmit.cta',
    defaultMessage: 'Resubmit for verification',
  });
  const submittingLabel = intl.formatMessage({
    id: 'payout.kyb.resubmit.submitting',
    defaultMessage: 'Resubmitting…',
  });
  const resubmitTitle = intl.formatMessage({
    id: 'payout.kyb.resubmit.title',
    defaultMessage: 'Correct and resubmit',
  });

  if (entityType === 'individual') {
    return (
      <KycForm
        initialValue={{ ...emptyKyc(), email: detail?.email ?? '' }}
        submitting={resubmit.isPending}
        submitLabel={submitLabel}
        submittingLabel={submittingLabel}
        heading={
          <div>
            <h2 className="text-base font-semibold text-navy-900">{resubmitTitle}</h2>
            <p className="mt-1 text-sm text-navy-600">
              {intl.formatMessage({
                id: 'onboarding.kyc.resubmit.subtitle',
                defaultMessage:
                  'Re-enter your identity details, re-upload your documents, then resubmit.',
              })}
            </p>
          </div>
        }
        upload={uploadOrgKybFile}
        onSubmit={(value) => resubmit.mutate(kycValueToInitiateKyb(value))}
      />
    );
  }

  return (
    <KybForm
      initialBiz={bizFromDetail(detail)}
      initialOwners={ownersFromDetail(detail)}
      submitting={resubmit.isPending}
      submitLabel={submitLabel}
      submittingLabel={submittingLabel}
      heading={
        <div>
          <h2 className="text-base font-semibold text-navy-900">{resubmitTitle}</h2>
          <p className="mt-1 text-sm text-navy-600">
            {intl.formatMessage({
              id: 'payout.kyb.resubmit.subtitle',
              defaultMessage:
                'We kept your previous details. Fix the items above, re-upload your documents, then resubmit.',
            })}
          </p>
        </div>
      }
      upload={uploadOrgKybFile}
      onSubmit={(value) => resubmit.mutate(kybValueToInitiateKyb(value))}
    />
  );
}

function KybPendingCard({
  kyb,
  detail,
}: {
  kyb: KybSubmissionView;
  detail: KybReceiverDetailView | null;
}): JSX.Element {
  const intl = useIntl();
  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-3 rounded-md border border-navy-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-navy-900">
            {intl.formatMessage({
              id: 'payout.kyb.pendingTitle',
              defaultMessage: 'Verification in progress',
            })}
          </h2>
          <KybStatusBadge status={kyb.status} />
        </div>
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'onboarding.kyb.pendingBody',
            defaultMessage:
              'Our settlement partner is reviewing your business. You can register your bank account as soon as verification is approved. We will not require any further input from you here.',
          })}
        </p>
      </div>
      <KybReceiverPanel detail={detail} />
    </div>
  );
}

function KybRejectedCard({
  kyb,
  detail,
}: {
  kyb: KybSubmissionView;
  detail: KybReceiverDetailView | null;
}): JSX.Element {
  const intl = useIntl();
  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-red-900">
            {intl.formatMessage({
              id: 'payout.kyb.rejectedTitle',
              defaultMessage: 'Verification rejected',
            })}
          </h2>
          <KybStatusBadge status={kyb.status} />
        </div>
        <p className="text-sm text-red-800">
          {intl.formatMessage({
            id: 'payout.kyb.rejectedBody',
            defaultMessage:
              'Our settlement partner rejected this submission. Review the items below, correct your details, and resubmit.',
          })}
        </p>
        <KybWarningsList
          kycWarnings={detail?.kycWarnings ?? []}
          fraudWarnings={detail?.fraudWarnings ?? []}
        />
      </div>
      {/* Remount once the receiver detail loads so the form seeds from it
          (KybForm captures initial values at mount). The key is stable after
          that, so typing isn't wiped by background refetches. */}
      <KybResubmitForm
        key={detail?.available ? detail.receiverId : 'pending'}
        entityType={kyb.entityType}
        detail={detail}
      />
    </div>
  );
}

// Edit onboarding info after approval, mirroring the resubmit-after-rejection
// form but posting to the update endpoint (which keeps the receiver approved
// server-side until re-review flips the submission to `verifying`). Documents
// and owners are optional on edit — the receiver already has them on file, so
// the org only needs to upload what it wants to replace.
function KybEditForm({
  entityType,
  detail,
  onDone,
}: {
  entityType: 'individual' | 'business';
  detail: KybReceiverDetailView | null;
  onDone: () => void;
}): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();

  const edit = useMutation({
    mutationFn: (payload: InitiateKybInput) => updateKyb(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyb-submission'] });
      void qc.invalidateQueries({ queryKey: ['kyb-receiver-detail'] });
      toast.success(
        intl.formatMessage({
          id: 'onboarding.edit.done',
          defaultMessage: 'Changes submitted — our settlement partner is reviewing them.',
        }),
      );
      onDone();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const submitLabel = intl.formatMessage({
    id: 'onboarding.edit.submit',
    defaultMessage: 'Submit changes',
  });
  const submittingLabel = intl.formatMessage({
    id: 'onboarding.edit.submitting',
    defaultMessage: 'Submitting…',
  });

  if (entityType === 'individual') {
    return (
      <KycForm
        initialValue={{ ...emptyKyc(), email: detail?.email ?? '' }}
        submitting={edit.isPending}
        submitLabel={submitLabel}
        submittingLabel={submittingLabel}
        heading={
          <div>
            <h2 className="text-base font-semibold text-navy-900">
              {intl.formatMessage({
                id: 'onboarding.edit.kyc.title',
                defaultMessage: 'Edit your information',
              })}
            </h2>
            <p className="mt-1 text-sm text-navy-600">
              {intl.formatMessage({
                id: 'onboarding.edit.kyc.subtitle',
                defaultMessage:
                  'Re-enter your identity details and documents; changes are re-reviewed by our settlement partner.',
              })}
            </p>
          </div>
        }
        upload={uploadOrgKybFile}
        onSubmit={(value) => edit.mutate(kycValueToInitiateKyb(value))}
      />
    );
  }

  return (
    <KybForm
      initialBiz={bizFromDetail(detail)}
      initialOwners={ownersFromDetail(detail)}
      docsOptional
      ownersOptional
      taxIdReadOnly
      submitting={edit.isPending}
      submitLabel={submitLabel}
      submittingLabel={submittingLabel}
      heading={
        <div>
          <h2 className="text-base font-semibold text-navy-900">
            {intl.formatMessage({
              id: 'onboarding.edit.title',
              defaultMessage: 'Edit business information',
            })}
          </h2>
          <p className="mt-1 text-sm text-navy-600">
            {intl.formatMessage({
              id: 'onboarding.edit.subtitle',
              defaultMessage:
                "Update your business details with our settlement partner. Documents are optional — upload only what you want to replace. Changes are re-reviewed; your verification may show as 'verifying' until re-approved.",
            })}
          </p>
          <p className="mt-2 text-xs text-navy-500">
            {intl.formatMessage({
              id: 'onboarding.edit.note',
              defaultMessage: 'You can keep operating while the review is in progress.',
            })}
          </p>
        </div>
      }
      upload={uploadOrgKybFile}
      onSubmit={(value) => edit.mutate(kybValueToUpdateKyb(value))}
    />
  );
}

// Approved: verification is complete. Show the receiver summary and point the
// org to the Bank account page to register where settlements land. The org
// can also edit its onboarding info in place — the panel swaps for a
// pre-filled form that posts to the update endpoint; a successful submit
// flips status to `verifying` server-side and the existing polling takes it
// from there.
function KybApprovedCard({
  detail,
  entityType,
}: {
  detail: KybReceiverDetailView | null;
  entityType: 'individual' | 'business';
}): JSX.Element {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);

  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-3 rounded-md border border-emerald-200 bg-emerald-50 p-4">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-emerald-900">
            {intl.formatMessage({
              id: 'onboarding.approved.title',
              defaultMessage: 'Business verified',
            })}
          </h2>
          <KybStatusBadge status="approved" />
          <div className="ml-auto">
            {editing ? (
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                {intl.formatMessage({
                  id: 'onboarding.edit.cancel',
                  defaultMessage: 'Cancel editing',
                })}
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => setEditing(true)}>
                {intl.formatMessage({
                  id: 'onboarding.edit.cta',
                  defaultMessage: 'Edit information',
                })}
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm text-emerald-800">
          {intl.formatMessage({
            id: 'onboarding.approved.body',
            defaultMessage:
              'Your business is verified. Register the bank account where Caratuva delivers your settlements to start collecting.',
          })}
        </p>
        <div className="flex justify-end">
          <Button asChild>
            <Link to="/bank-account">
              {intl.formatMessage({
                id: 'onboarding.approved.cta',
                defaultMessage: 'Go to Bank account',
              })}
            </Link>
          </Button>
        </div>
      </div>
      {editing ? (
        // Remount once the receiver detail loads so the form seeds from it
        // (KybForm captures initial values at mount). The key is stable after
        // that, so typing isn't wiped by background refetches.
        <KybEditForm
          key={detail?.available ? detail.receiverId : 'pending'}
          entityType={entityType}
          detail={detail}
          onDone={() => setEditing(false)}
        />
      ) : (
        <KybReceiverPanel detail={detail} />
      )}
    </div>
  );
}

// The rejection reasons our settlement partner reported (kyc + fraud warnings).
function KybWarningsList({
  kycWarnings,
  fraudWarnings,
}: {
  kycWarnings: KybReceiverWarning[];
  fraudWarnings: KybReceiverWarning[];
}): JSX.Element {
  const intl = useIntl();
  if (kycWarnings.length === 0 && fraudWarnings.length === 0) {
    return (
      <p className="text-sm text-red-800">
        {intl.formatMessage({
          id: 'payout.kyb.warnings.none',
          defaultMessage:
            'No specific reasons were provided. Double-check your business details and documents, then resubmit.',
        })}
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {kycWarnings.length > 0 ? (
        <WarningGroup
          title={intl.formatMessage({
            id: 'payout.kyb.warnings.compliance',
            defaultMessage: 'Compliance issues',
          })}
          warnings={kycWarnings}
        />
      ) : null}
      {fraudWarnings.length > 0 ? (
        <WarningGroup
          title={intl.formatMessage({
            id: 'payout.kyb.warnings.fraud',
            defaultMessage: 'Fraud checks',
          })}
          warnings={fraudWarnings}
        />
      ) : null}
    </div>
  );
}

function WarningGroup({
  title,
  warnings,
}: {
  title: string;
  warnings: KybReceiverWarning[];
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-red-900">{title}</h4>
      <ul className="space-y-1.5">
        {warnings.map((w, i) => (
          <li
            key={i}
            className="rounded-md border border-red-200 bg-white/60 p-2 text-sm text-red-900"
          >
            <span className="font-medium">{w.message || w.code || 'Issue'}</span>
            {w.code && w.message ? (
              <span className="ml-1 text-xs text-red-700">({w.code})</span>
            ) : null}
            {w.resolutionStatus ? (
              <span className="ml-2 text-xs uppercase tracking-wide text-red-600">
                {w.resolutionStatus}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
